import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: 'Accueil', href: 'hero' },
    { label: 'Types de Fenêtres', href: 'window-types' },
    { label: 'À propos', href: 'about' },
    { label: 'Réalisations', href: 'portfolio' },
    { label: 'FAQ', href: 'faq' },
    { label: 'Contact', href: 'contact' }
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-2'
        }`}
        role="navigation"
        aria-label="Navigation principale"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 md:h-16">
            {/* Logo */}
            <motion.div 
              className="flex items-center gap-2 md:gap-3 cursor-pointer" 
              onClick={() => scrollToSection('hero')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <img 
                src="https://public.readdy.ai/ai/img_res/a85411f8-cb5d-4bf1-83b4-29220f57d805.png" 
                alt="FenetrePro Toulouse - Réparation fenêtres professionnelle"
                title="FenetrePro Toulouse - Expert vitrier"
                className="h-9 md:h-10 w-auto"
                loading="eager"
                width="48"
                height="48"
              />
              <div className="flex flex-col">
                <h1 className="text-base md:text-xl font-bold text-gray-900">
                  FenetrePro
                </h1>
                <span className="text-xs md:text-sm font-medium text-teal-600">
                  Toulouse
                </span>
              </div>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="/" className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer">
                Accueil
              </a>
              <a href="/tarifs" className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer">
                Tarifs
              </a>
              <a href="/#services" className="text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer">
                Services
              </a>
              {navItems.map((item, index) => (
                <motion.button
                  key={item.href}
                  onClick={() => scrollToSection(item.href)}
                  className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600 relative group"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.3 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-teal-600 transition-all duration-300 group-hover:w-full"></span>
                </motion.button>
              ))}
              <motion.a
                href="tel:0554542545"
                className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 py-2.5 rounded-full font-medium hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.3 }}
                aria-label="Appeler FenetrePro au 05 54 54 25 45"
              >
                <i className="ri-phone-line mr-2"></i>
                05 54 54 25 45
              </motion.a>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg cursor-pointer text-gray-700 hover:bg-gray-100"
              whileTap={{ scale: 0.9 }}
              aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={isMobileMenuOpen}
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </div>
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-white shadow-lg border-t border-gray-100 overflow-hidden"
            >
              <div className="px-4 py-4 space-y-2">
                <a
                  href="/"
                  className="block text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer"
                >
                  Accueil
                </a>
                <a
                  href="/tarifs"
                  className="block text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer"
                >
                  Tarifs
                </a>
                <a
                  href="/#services"
                  className="block text-gray-700 hover:text-teal-600 transition-colors font-medium cursor-pointer"
                >
                  Services
                </a>
                {navItems.map((item, index) => (
                  <motion.button
                    key={item.href}
                    onClick={() => scrollToSection(item.href)}
                    className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors cursor-pointer font-medium"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {item.label}
                  </motion.button>
                ))}
                <motion.a
                  href="tel:0554542545"
                  className="block w-full text-center bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 py-3 rounded-full font-medium hover:from-teal-600 hover:to-teal-700 transition-all cursor-pointer mt-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: navItems.length * 0.05, duration: 0.3 }}
                  whileTap={{ scale: 0.98 }}
                  aria-label="Appeler FenetrePro"
                >
                  <i className="ri-phone-line mr-2"></i>
                  05 54 54 25 45
                </motion.a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};

export default Navigation;
